import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "contentAreaFlush", "headerComponent", "headerWrapperStyles", "horizontalDivider", "isEditor", "MainSection", "mainSectionStyles", "mainSectionBackgroundColor", "mainSectionProps", "narrowLayout", "navHeight", "pageBackgroundColor", "pageLayout", "sidebarAreaFlush", "sidebarComponent", "stickySidebar", "tabs", "title", "pageMaxWidth", "mainStyles"];
import styled, { useTheme } from 'foundations-theming/utils';
import { AbstractPageGridContent } from '../AbstractPageGridContent/AbstractPageGridContent';
import { AbstractPageSection } from '../AbstractPageSection/AbstractPageSection';
import { Header } from '../Header/Header';
import * as CustomRenderer from './customRenderer';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { toPx } from '../../../utils/toPx';
import { VENUS_LAYER } from '../../constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const NAV_BAR_HEIGHT = 'var(--uicomponents-global-nav-height, 54px)';

// Helper function to resolve color tokens to CSS values or return already-resolved values
export const resolveColorTokenWithTheme = theme => colorValue => {
  if (!colorValue) return undefined;

  // Check if the value is a theme token by seeing if it exists in theme.color
  if (colorValue in theme.color) {
    return theme.color[colorValue];
  }

  // If it's not a theme token, assume it's already a resolved CSS color value
  return colorValue;
};
const StyledPage = styled.div.withConfig({
  displayName: "AbstractPageTemplate__StyledPage",
  componentId: "sc-1wro6xx-0"
})(({
  $navHeight,
  $backgroundColor,
  $cssStyles,
  theme
}) => {
  const minHeight = $navHeight && $navHeight >= 0 ? toPx($navHeight) : NAV_BAR_HEIGHT;
  return Object.assign({
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: $backgroundColor || theme.color['fill-surface-default'],
    minHeight: `calc(100vh - ${minHeight})`
  }, $cssStyles);
});
const StyledHeaderWrapper = styled.div.withConfig({
  displayName: "AbstractPageTemplate__StyledHeaderWrapper",
  componentId: "sc-1wro6xx-1"
})(({
  $isEditor,
  $cssStyles
}) => {
  return Object.assign({
    paddingLeft: 0,
    paddingRight: 0,
    zIndex: $isEditor ? VENUS_LAYER : undefined
  }, $cssStyles);
});
const StyledMainWrapperEditorStyles = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 0,
  height: 'auto'
};
const StyledMainWrapper = styled.div.withConfig({
  displayName: "AbstractPageTemplate__StyledMainWrapper",
  componentId: "sc-1wro6xx-2"
})(({
  $backgroundColor,
  $isEditor,
  $divided,
  theme
}) => {
  return Object.assign({
    flex: '1 0 auto',
    backgroundColor: $backgroundColor,
    borderBlockStart: $divided ? `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}` : undefined
  }, $isEditor && StyledMainWrapperEditorStyles);
});
const StyledPageSectionWrapper = styled.div.withConfig({
  displayName: "AbstractPageTemplate__StyledPageSectionWrapper",
  componentId: "sc-1wro6xx-3"
})(({
  $contentAreaFlush,
  $mainSectionCssStyles,
  theme
}) => {
  return Object.assign({
    flexGrow: $contentAreaFlush ? 1 : undefined,
    position: $contentAreaFlush ? 'relative' : undefined,
    paddingTop: $contentAreaFlush ? 0 : theme.space['500'],
    display: 'flex',
    flexDirection: 'column'
  }, $mainSectionCssStyles);
});
export const AbstractPageTemplate = _ref => {
  let {
      children,
      contentAreaFlush = false,
      headerComponent = /*#__PURE__*/_jsx(Header, {}),
      headerWrapperStyles = {},
      horizontalDivider = false,
      isEditor = false,
      MainSection = AbstractPageGridContent,
      mainSectionStyles = {},
      mainSectionBackgroundColor,
      mainSectionProps,
      narrowLayout = false,
      navHeight,
      pageBackgroundColor,
      pageLayout = 'max-width',
      sidebarAreaFlush = false,
      sidebarComponent,
      stickySidebar = false,
      tabs,
      title,
      pageMaxWidth,
      mainStyles
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  const resolvedMainSectionBackgroundColor = resolveColorTokenWithTheme(theme)(mainSectionBackgroundColor);
  const sidebarProps = {
    mainStyles,
    sidebarAreaFlush,
    sidebarComponent,
    stickySidebar
  };
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const pageMaxWidthPx = pageMaxWidth ? toPx(pageMaxWidth) : undefined;

  // CSS Styles for StyledHeaderWrapper
  const headerCssStyles = useMapTokenPropsToCSS(headerWrapperStyles);
  const mainSectionCssStyles = useMapTokenPropsToCSS(mainSectionStyles);
  return /*#__PURE__*/_jsxs(StyledPage, Object.assign({
    $cssStyles: cssStyles,
    $navHeight: navHeight,
    $backgroundColor: pageBackgroundColor
  }, domProps, {
    children: [headerComponent && /*#__PURE__*/_jsx(StyledHeaderWrapper, {
      "data-test-id": "abstract-page-template-header",
      $isEditor: isEditor,
      $cssStyles: headerCssStyles,
      children: /*#__PURE__*/_jsxs(AbstractPageSection, {
        fullWidth: pageLayout === 'full-width',
        isEditor: isEditor,
        centered: pageLayout === 'max-width',
        contentAreaFlush: contentAreaFlush,
        headerComponent: headerComponent,
        maxInlineSize: pageMaxWidthPx,
        children: [CustomRenderer.render(headerComponent, {
          title,
          use: 'condensed',
          flush: true
        }), tabs]
      })
    }), /*#__PURE__*/_jsx(StyledMainWrapper, {
      $backgroundColor: resolvedMainSectionBackgroundColor,
      $isEditor: isEditor,
      $divided: horizontalDivider,
      children: /*#__PURE__*/_jsx(StyledPageSectionWrapper, {
        $contentAreaFlush: contentAreaFlush,
        $mainSectionCssStyles: mainSectionCssStyles,
        children: /*#__PURE__*/_jsx(AbstractPageSection, {
          fullWidth: pageLayout === 'full-width',
          centered: pageLayout === 'max-width',
          isEditor: isEditor,
          narrow: narrowLayout,
          contentAreaFlush: contentAreaFlush,
          maxInlineSize: pageMaxWidthPx,
          children: /*#__PURE__*/_jsx(MainSection, Object.assign({
            backgroundColor: resolvedMainSectionBackgroundColor
          }, sidebarProps, mainSectionProps, {
            children: children
          }))
        })
      })
    })]
  }));
};